/**
 * Reading a plan record out of untrusted JSON — a backup file.
 *
 * The editor only ever writes documents it built itself, but a backup can be
 * hand-edited, truncated, or not a backup at all. This is the port of the
 * server's `PlanLayoutIn` validation (field types, bounds, the per-shape seat
 * cap), finished with the same `validateDoc` the save path uses, so a restored
 * plan is one the editor could have saved. Anything wrong rejects the record
 * rather than repairing it: guessing at someone's seating plan is worse than
 * saying the file is damaged.
 */

import { TABLE_COLORS } from '../colors'
import { LIMITS } from '../config'
import { isCalendarDate } from '../dates'
import { EVENT_KINDS } from '../events'
import { MAX_SEATS, MIN_SEATS, maxSeatCount } from '../geometry'
import { isLocale } from '../i18n'
import { validateDoc } from '../merge'
import {
  ApiError,
  type EventKind,
  type Guest,
  type Rsvp,
  type Seat,
  type SeatLabelMode,
  type Table,
  type TableColor,
  type TableShape,
} from '../types'
import type { StoredPlan } from './db'

const SHAPES: readonly TableShape[] = ['round', 'square', 'rectangle', 'sweetheart']
const RSVPS: readonly Rsvp[] = ['pending', 'yes', 'no']
const SEAT_LABELS: readonly SeatLabelMode[] = ['name', 'initials', 'none']
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const COORDINATE_LIMIT = LIMITS.maxCoordinate

class Invalid extends Error {}

const fail = (path: string, why: string): never => {
  throw new Invalid(`${path}: ${why}`)
}

function object(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) fail(path, 'not an object')
  return value as Record<string, unknown>
}

function array(value: unknown, path: string, max: number): unknown[] {
  if (!Array.isArray(value)) fail(path, 'not a list')
  const list = value as unknown[]
  if (list.length > max) fail(path, `more than ${max} entries`)
  return list
}

function text(value: unknown, path: string, max: number, min = 0): string {
  if (typeof value !== 'string') fail(path, 'not text')
  const s = value as string
  if (s.length > max) fail(path, `longer than ${max}`)
  if (s.trim().length < min) fail(path, 'blank')
  return s
}

const optionalText = (value: unknown, path: string, max: number): string | null =>
  value === null || value === undefined ? null : text(value, path, max)

function finite(value: unknown, path: string, low = -Infinity, high = Infinity): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'not a finite number')
  const n = value as number
  if (n < low || n > high) fail(path, `outside ${low}..${high}`)
  return n
}

function integer(value: unknown, path: string, low: number, high: number): number {
  const n = finite(value, path, low, high)
  if (!Number.isInteger(n)) fail(path, 'not a whole number')
  return n
}

const clamp = (n: number, low: number, high: number): number => Math.min(Math.max(n, low), high)

function oneOf<T extends string>(value: unknown, path: string, allowed: readonly T[]): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) fail(path, 'unknown value')
  return value as T
}

function id(value: unknown, path: string): string {
  if (typeof value !== 'string' || !UUID.test(value)) fail(path, 'not an id')
  return (value as string).toLowerCase()
}

const optionalId = (value: unknown, path: string): string | null =>
  value === null || value === undefined ? null : id(value, path)

function timestamp(value: unknown, path: string): string {
  const s = text(value, path, 40)
  if (Number.isNaN(Date.parse(s))) fail(path, 'not a date-time')
  return s
}

function seat(raw: unknown, path: string): Seat {
  const r = object(raw, path)
  return {
    id: id(r.id, `${path}.id`),
    position: integer(r.position, `${path}.position`, 0, MAX_SEATS - 1),
    guestId: optionalId(r.guestId, `${path}.guestId`),
    label: seatLabel(r.label, `${path}.label`),
  }
}

/**
 * Checked, never kept. The server stored a chair label, but the editor has none
 * to show or clear — so one arriving in a file would print in the PDF on a
 * chair the planner sees as empty.
 */
function seatLabel(value: unknown, path: string): null {
  optionalText(value, path, 80)
  return null
}

function table(raw: unknown, path: string): Table {
  const r = object(raw, path)
  const shape = oneOf(r.shape, `${path}.shape`, SHAPES)
  const seatCount = integer(r.seatCount, `${path}.seatCount`, MIN_SEATS, maxSeatCount(shape))
  return {
    id: id(r.id, `${path}.id`),
    name: text(r.name, `${path}.name`, 80, 1),
    shape,
    color: oneOf<TableColor>(r.color, `${path}.color`, TABLE_COLORS),
    x: finite(r.x, `${path}.x`, -COORDINATE_LIMIT, COORDINATE_LIMIT),
    y: finite(r.y, `${path}.y`, -COORDINATE_LIMIT, COORDINATE_LIMIT),
    rotation: ((finite(r.rotation, `${path}.rotation`) % 360) + 360) % 360,
    seatCount,
    zIndex: integer(r.zIndex, `${path}.zIndex`, -LIMITS.maxZIndex, LIMITS.maxZIndex),
    seats: array(r.seats, `${path}.seats`, MAX_SEATS).map((s, i) => seat(s, `${path}.seats[${i}]`)),
  }
}

function guest(raw: unknown, path: string): Guest {
  const r = object(raw, path)
  return {
    id: id(r.id, `${path}.id`),
    fullName: text(r.fullName, `${path}.fullName`, 120, 1),
    groupLabel: optionalText(r.groupLabel, `${path}.groupLabel`, 60),
    note: optionalText(r.note, `${path}.note`, 2000),
    rsvp: oneOf(r.rsvp, `${path}.rsvp`, RSVPS),
    photoAssetId: optionalId(r.photoAssetId, `${path}.photoAssetId`),
    photoUrl: null,
  }
}

/**
 * @throws ApiError `backup_invalid` naming the first problem found
 */
export function parseStoredPlan(raw: unknown, path = 'plan'): StoredPlan {
  try {
    const r = object(raw, path)
    const viewport = object(r.viewport, `${path}.viewport`)
    const settings = object(r.settings, `${path}.settings`)
    const defaults = object(settings.defaultSeats, `${path}.settings.defaultSeats`)
    const seatDefault = (shape: TableShape): number =>
      integer(defaults[shape], `${path}.settings.defaultSeats.${shape}`, MIN_SEATS, maxSeatCount(shape))

    const eventDate = optionalText(r.eventDate, `${path}.eventDate`, 10)
    if (eventDate !== null && !isCalendarDate(eventDate)) fail(`${path}.eventDate`, 'not a YYYY-MM-DD day')
    if (typeof r.language !== 'string' || !isLocale(r.language)) {
      fail(`${path}.language`, 'unknown language')
    }

    const plan: StoredPlan = {
      id: id(r.id, `${path}.id`),
      title: text(r.title, `${path}.title`, 160, 1).trim(),
      // Backups made before kinds existed hold weddings, as everything did then.
      eventKind:
        r.eventKind === undefined
          ? 'wedding'
          : oneOf<EventKind>(r.eventKind, `${path}.eventKind`, EVENT_KINDS),
      eventDate,
      venue: optionalText(r.venue, `${path}.venue`, 200),
      language: r.language as StoredPlan['language'],
      // Read for shape only: a version counts saves in *this* browser, so
      // `restoreBackup` sets its own and never stores the file's.
      version: integer(r.version, `${path}.version`, 1, Infinity),
      createdAt: timestamp(r.createdAt, `${path}.createdAt`),
      updatedAt: timestamp(r.updatedAt, `${path}.updatedAt`),
      // Where the camera was: no part of the plan, and re-clamped on open. So
      // brought into range rather than refused — zoomed right out on a small
      // screen, or panned out to far tables, the editor saves values past it.
      viewport: {
        x: clamp(finite(viewport.x, `${path}.viewport.x`), -COORDINATE_LIMIT, COORDINATE_LIMIT),
        y: clamp(finite(viewport.y, `${path}.viewport.y`), -COORDINATE_LIMIT, COORDINATE_LIMIT),
        zoom: clamp(finite(viewport.zoom, `${path}.viewport.zoom`), 0.05, 6),
      },
      settings: {
        defaultSeats: {
          round: seatDefault('round'),
          square: seatDefault('square'),
          rectangle: seatDefault('rectangle'),
          sweetheart: seatDefault('sweetheart'),
        },
        defaultTableColor: oneOf<TableColor>(
          settings.defaultTableColor,
          `${path}.settings.defaultTableColor`,
          TABLE_COLORS,
        ),
        seatLabels: oneOf(settings.seatLabels, `${path}.settings.seatLabels`, SEAT_LABELS),
      },
      tables: array(r.tables, `${path}.tables`, LIMITS.maxTablesPerPlan).map((t, i) =>
        table(t, `${path}.tables[${i}]`),
      ),
      guests: array(r.guests, `${path}.guests`, LIMITS.maxGuestsPerPlan).map((g, i) =>
        guest(g, `${path}.guests[${i}]`),
      ),
    }

    const problems = validateDoc(plan)
    if (problems.length > 0) fail(path, problems[0])
    return plan
  } catch (error) {
    if (error instanceof Invalid) throw new ApiError(422, 'backup_invalid', error.message)
    throw error
  }
}
